/**
 * Hook to load supervision timeline / audit / edit history from Medplum
 * Provenance resources in live mode, or local mock data in mock mode.
 *
 * FHIR model:
 *   Provenance.target       → QR being reviewed
 *   Provenance.agent        → actor (supervisor, clinician)
 *   Provenance.recorded     → timestamp
 *   Provenance.activity     → action type (comment-added, reviewed, signed, etc.)
 *   Provenance.extension    → detail text / summary
 */

import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Provenance } from '@medplum/fhirtypes';
import type { AuditEntry } from '../types';
import {
  PROVENANCE_DETAIL_EXT_URL,
  SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM,
} from '../supervisionFhirConstants';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

type NoteEditEntry = {
  id: string;
  timestamp: string;
  actorId: string;
  actorName: string;
  description: string;
  summary?: string;
};

function extractRefId(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  const match = ref.match(/\w+\/([^/]+)$/);
  return match?.[1];
}

function provenanceToAuditEntry(prov: Provenance): AuditEntry | null {
  const activityCode = prov.activity?.coding?.find(
    (c) => c.system === SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM,
  )?.code;

  const actionMap: Record<string, AuditEntry['action']> = {
    'comment-added': 'commented',
    'comment-resolved': 'reviewed',
    'requested-edits': 'requested-edits',
    reviewed: 'reviewed',
    signed: 'signed',
    'note-edited': 'reviewed',
  };

  const action = activityCode ? actionMap[activityCode] : undefined;
  if (!action) return null;

  const agent = prov.agent?.[0];
  const detailExt = prov.extension?.find((e) => e.url === PROVENANCE_DETAIL_EXT_URL);

  return {
    id: prov.id!,
    action,
    actorId: extractRefId(agent?.who?.reference) ?? '',
    actorName: agent?.who?.display ?? '—',
    timestamp: prov.recorded ?? '',
    details: detailExt?.valueString ?? prov.activity?.text,
  };
}

function provenanceToNoteEdit(prov: Provenance): NoteEditEntry | null {
  const activityCode = prov.activity?.coding?.find(
    (c) => c.system === SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM,
  )?.code;

  if (activityCode !== 'note-edited') return null;

  const agent = prov.agent?.[0];
  const detailExt = prov.extension?.find((e) => e.url === PROVENANCE_DETAIL_EXT_URL);

  return {
    id: prov.id!,
    timestamp: prov.recorded ?? '',
    actorId: extractRefId(agent?.who?.reference) ?? '',
    actorName: agent?.who?.display ?? '—',
    description: prov.activity?.text ?? 'Note edited',
    summary: detailExt?.valueString,
  };
}

export interface UseSupervisionTimelineResult {
  auditEntries: AuditEntry[];
  noteEdits: NoteEditEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSupervisionTimeline(
  documentId: string | undefined,
): UseSupervisionTimelineResult {
  const medplum = useMedplum();
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [noteEdits, setNoteEdits] = useState<NoteEditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!documentId) {
      setAuditEntries([]);
      setNoteEdits([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const provenances = await medplum.searchResources('Provenance', {
        target: `QuestionnaireResponse/${documentId}`,
        _sort: '-recorded',
        _count: '100',
      });

      const audits: AuditEntry[] = [];
      const edits: NoteEditEntry[] = [];

      for (const prov of provenances) {
        const audit = provenanceToAuditEntry(prov);
        if (audit) audits.push(audit);

        const edit = provenanceToNoteEdit(prov);
        if (edit) edits.push(edit);
      }

      setAuditEntries(audits);
      setNoteEdits(edits);
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load timeline'));
      setAuditEntries([]);
      setNoteEdits([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, documentId]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { auditEntries, noteEdits, loading, error, refetch: doFetch };
}
